/**
 * L.4 — NIPE tool: explainLora
 *
 * Read-only. Returns the canonical "what is a specialization (LoRA)"
 * primer text, optionally tuned for an audience (attorney | operator).
 *
 * Source of truth: `src/topics/lora.md`. The body here mirrors the
 * markdown's structure so the chat response stays consistent with the
 * NIPE topic tile and the (?) popover.
 *
 * No external context — pure response. Stays a leaf module per the
 * NIPE tool contract.
 */

import type { NipeTool } from './types';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ExplainLoraContext {}

const ATTORNEY_BODY = `A **specialization** is a small adjustment to the base model that focuses it on certain patterns and de-emphasizes others. We build one from the Skills you pick.

It is **additive and subtractive**:
- *Additive*: amplifies the words, structures, and citation styles that show up across the Skills you trained on.
- *Subtractive*: quiets the moves that show up less in your Skills than in the base model's average behavior.

What it is NOT:
- Not new legal knowledge — the base model already knows the law.
- Not memorization — we don't train it to recite your Skills.
- Not permanent — it's a thin overlay (a few MB) you can take off any time.

How to see what it does: open the **Specialization Lab**. It runs the same prompt twice — once on the base model, once with your specialization layered on — and shows the two outputs side by side. The LoRA-strength slider scrubs between pure-base (0%) and full-specialization (100%).

In operator-facing parts of the app this is called a **LoRA** (Low-Rank Adaptation). Same thing, technical name.`;

const OPERATOR_BODY = `A LoRA (Low-Rank Adaptation) is a small set of weight deltas trained on top of a frozen base model. On this portal:

- Trained per-skill or per-skill-bundle via the Tinker pipeline (\`tinkerTrainingJobs\` → ACA Job → Tinker API → \`lorasByPortal\`).
- Default config: rank-8, 1 epoch, learningRate 0.0002, costCapUsd 5.
- Inference layered on via \`inferenceJobs.request\` with \`tinkerLoraUrl\` — same ACA Job re-fires in MODE=sample.
- User-facing copy refers to this as a "specialization" — see \`src/topics/lora.md\` for the framing.

Surfaces: TrainLoraButton (per-skill), TinkerTab (operator-grade firm-wide), SpecializationLabWindow (compare). Storage: \`lorasByPortal\` table, indexed by portalSlug.`;

export const explainLoraTool: NipeTool<ExplainLoraContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'explainLora',
      description:
        "Return the canonical primer for 'what is a specialization (LoRA)?'. Use when the user asks how training works or what specialization does. Audience-tuned: 'attorney' (default) for plain language, 'operator' for technical surface details.",
      parameters: {
        type: 'object',
        properties: {
          audience: {
            type: 'string',
            description:
              "Optional. 'attorney' (default) for plain-language framing; 'operator' for technical Repancy surface details.",
          },
        },
        required: [],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: false,
  handler: async (args, _ctx) => {
    const audience = typeof args.audience === 'string' ? args.audience.toLowerCase() : 'attorney';
    if (audience === 'operator') {
      return { audience: 'operator', body: OPERATOR_BODY };
    }
    return { audience: 'attorney', body: ATTORNEY_BODY };
  },
};
